<template>
  <div>
    <trac-loading v-if="loading" />
    <div class="flex justify-between items-center mt-8 mb-6">
      <h1 class="text-xl font-bold text-accentDark">
        {{ isCreateMode ? 'Create' : '' }} Modifier set{{
          isCreateMode ? '' : 's'
        }}
      </h1>
      <trac-button
        v-if="!isCreateMode"
        class="text-primaryBlue text-sm font-bold"
        variant="plain"
        @button-clicked="isCreateMode = true"
      >
        CREATE NEW
      </trac-button>
      <button
        v-if="isCreateMode"
        class="text-primaryBlue text-sm font-bold"
        @click="isCreateMode = false"
      >
        Back
      </button>
    </div>

    <div v-if="!isCreateMode" class="flex flex-col gap-3">
      <div
        v-for="(modifier, index) in modifierSets"
        :key="modifier.id"
        class="pb-4"
        :class="{
          'border-b-2 border-b-primaryGray':
            modifierSets && modifierSets.length - 1 !== index,
        }"
      >
        <div
          class="flex justify-between text-sm cursor-pointer"
          role="button"
          @click="selectedIndex = selectedIndex !== index ? index : null"
        >
          <h4 class="text-sm flex items-center gap-1">
            <span :class="{ 'transform rotate-90': selectedIndex === index }">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 3L14 10L6 17"
                  stroke="#909090"
                  stroke-width="1.16667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <p class="capitalize">{{ modifier.name }}</p>
          </h4>
          <div class="flex flex-col items-end">
            <p>
              {{ modifier.modifiers.length }} modifier{{
                modifier.modifiers.length <= 1 ? '' : 's'
              }}
            </p>
            <p v-if="!inViewMode" class="text-xs text-primaryGray">
              {{ modifiersSelectedInGroup(index) }} selected
            </p>
          </div>
        </div>
        <div
          v-if="selectedIndex === index"
          class="mt-3 shadow rounded-sm p-4 grid grid-cols-12 gap-4"
        >
          <div
            class="col-span-4 text-sm flex items-center gap-1"
            v-for="mod in modifier.modifiers"
            :key="mod._id"
          >
            <trac-checkbox
              v-if="!inViewMode"
              :isChecked="selectedModifiers.includes(mod._id)"
              @check="toggleChecked(mod._id)"
            >
              <p class="text-xs ml-1">{{ mod.name }}</p>
            </trac-checkbox>
            <p v-else class="text-xs ml-1">{{ mod.name }}</p>
          </div>
        </div>
      </div>
      <div v-if="!inViewMode" class="flex justify-between items-center">
        <p class="text-primaryGray text-sm">
          {{ selectedModifiers.length }} modifier{{
            selectedModifiers.length <= 1 ? '' : 's'
          }}
          selected
        </p>
        <trac-button
          v-if="!isCreateMode"
          class="text-primaryBlue text-sm font-bold"
          variant="outline"
          :disabled="!selectedModifiers.length"
          @button-clicked="addModifiersToProduct"
        >
          SAVE MODIFIERS
        </trac-button>
      </div>
    </div>
    <create-modifier-set v-else @close="refetch" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { eventBus } from '../../eventBus';
import CreateModifierSet from './CreateModifierSet.vue';

export default {
  components: { CreateModifierSet },
  name: 'ModifierSets',
  emits: ['update-modifiers', 'add-modifier-sets'],
  props: {
    product: {
      type: Object,
      default: () => {},
    },
    inViewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modifierSets: [],
      selectedModifiers: [],
      selectedIndex: null,
      isCreateMode: false,
      loading: false,
    };
  },
  created() {
    this.fetchModifierSets();
    this.selectedModifiers = [
      ...(this.product?.modifiers?.map((m) => m?.id || m) ?? []),
    ];
  },
  methods: {
    ...mapActions(['FETCH_MODIFIER_SETS']),
    async fetchModifierSets() {
      this.loading = true;
      try {
        const res = await this.FETCH_MODIFIER_SETS();
        this.modifierSets = (res.data ?? []).filter((mod) => !!mod.modifiers);
        this.$emit('add-modifier-sets', this.modifierSets);
      } catch (error) {
        eventBus.$emit('trac-alert', {
          message: error?.res?.data?.message || error?.message,
        });
      } finally {
        this.loading = false;
      }
    },
    refetch() {
      this.isCreateMode = false;
      this.fetchModifierSets();
    },
    modifiersSelectedInGroup(index) {
      const modifier = this.modifierSets?.[index];
      return (
        modifier.modifiers?.filter((mod) =>
          this.selectedModifiers.includes(mod._id)
        ).length ?? 0
      );
    },
    toggleChecked(id) {
      const indexMatch = this.selectedModifiers.findIndex((idx) => idx === id);
      if (indexMatch !== -1) {
        this.selectedModifiers.splice(indexMatch, 1);
        return;
      }
      this.selectedModifiers = this.selectedModifiers.concat(id);
    },
    addModifiersToProduct() {
      this.$emit('update-modifiers', this.selectedModifiers);
    },
  },
};
</script>

<style scoped></style>
